// Angular
import { Injectable } from '@angular/core';
// Services
import { ViewsService } from '../../../../../../services/views.service';
import { ExternalApiService } from '../../../../../services/external-api.service';
import { BroadcastGeneralEventMetadata } from '../../../models/component.events';
import { BroadcastService } from '@cv-invision/core';
// Other
import { Subject } from 'rxjs';
// Model
import { ToasterType } from '@cv-invision/core';

@Injectable({
	providedIn: 'root'
})
export class SmartTagsService {
	smartTagDataChanged: Subject<any> = new Subject();

	get order(): any {
		return this.viewsService.order;
	}

	get photosSelected(): any {
		return this.viewsService.photosSelected;
	}

	constructor(
		private viewsService: ViewsService,
		private externalApiService: ExternalApiService,
		private broadcastService: BroadcastService
	) {
		//
	}

	/**
	 * service to delete a smart tag
	 * @param params
	 * @returns
	 */
	public async deleteSmartTag(smartTagPhoto: any, version = 'v2'): Promise<any> {
		const response = await this.externalApiService.deleteSmartTagById(this.order.id, smartTagPhoto.smartTagObjectId).toPromise();
		if (response && response.deleted) {

			this.broadcastService.broadcast({
				name: ToasterType.TOASTER,
				event: {
					type: 'warning',
					title: 'Smart tag deleted',
					message: 'Smart Tag was deleted'
				}
			});

			const parentJsonPath = await this.deleteSmartTagImage(smartTagPhoto);

			const broadcastMetadata = new BroadcastGeneralEventMetadata();
			broadcastMetadata.data = response.jsonParent || [];
			broadcastMetadata.path = parentJsonPath.path;
			broadcastMetadata.isReplace = true;
			broadcastMetadata['deleted'] = true;
			return broadcastMetadata;
		}
	}

	/**
	 * Event to remove smart tag photo
	 */

	private async deleteSmartTagImage(smartTagPhoto: any, version = 'v2') {
		const parentJsonPath = this.getParentPath(smartTagPhoto.refJsonPath, version);

		const newPhotos = await this.externalApiService.getAttachments(this.order.id, parentJsonPath.path).toPromise();
		this.viewsService.patchPhotos(parentJsonPath.path, newPhotos.data);

		// Delete it from Selected photo if exists
		const photoSelectedIndex = this.photosSelected.findIndex((item: any) => {
			return item === smartTagPhoto.id;
		});
		if (photoSelectedIndex >= 0) {
			this.photosSelected.splice(photoSelectedIndex, 1);
		}

		return parentJsonPath;
	}

	/**
	 * refactor to use dynamic value in spliter(could be in options for the component)
	 * @param jsonPath get parentPath to redraw the section
	 * @param version
	 * @returns
	 */
	public getParentPath(jsonPath: string, version: string): any {
		const parentPath = [];
		const splitter = version == 'v1' ? '.smartTags.' : '.smartTags';
		const jsonPathSplited = jsonPath.split(splitter);
		const smartTagPath = jsonPathSplited[1];

		parentPath.push(jsonPathSplited[0]);
		parentPath.push('smartTags');

		if (smartTagPath && smartTagPath.match(/[a-z]+/g)) {
			const bucket = smartTagPath.replace(/\[[\d+]\]/g, '');
			parentPath.push(bucket);
		}

		return {
			bucket: parentPath[parentPath.length - 1],
			path: parentPath.join('.')
		};
	}

	/**
	 * method to get smart tag types for a specific room
	 * @param data
	 * @param smartTagTypeList
	 * @returns
	 */
	public getSmartTagTypes(data, smartTagTypeList, imageless: boolean = false): any[] {
		const roomMetaData = {
			ext: (
				(data.position === 'Exterior' || data.position === 'EXTERIOR') ||
				// if the data has address or Identification
				(data.address || data.identification)
			),
			int: (data.position === 'Interior' || data.position === 'INTERIOR'),
			both: ((data.position || '').toUpperCase() === 'ANY'),
			park: (data.isParking === true || data.isParking === 'true'),
			roomName: data.roomName
		};

		return this.filterSmartTagTypes(smartTagTypeList, roomMetaData, imageless);
	}

	/**
	 * Filter all SmartTag Types associated to this room
	 * @param smartTagTypes
	 * @param roomMetaData
	 * @private
	 */
	private filterSmartTagTypes(smartTagTypes: any, roomMetaData: any, imageless: boolean): any {
		const list = [];
		smartTagTypes.forEach((smartTagType: any) => {
			if (smartTagType.children) {
				const result = this.filterSmartTagTypes(smartTagType.children, roomMetaData, imageless);
				const valid = (
					(result && result.length > 0) && this.isValidSmartTagForRoom(smartTagType, roomMetaData)
				);
				if (valid) {
					// Filtered based on each room
					smartTagType.children = result;
					list.push(smartTagType);
				}
			} else if (this.isValidSmartTagForRoom(smartTagType, roomMetaData)) {
				if(imageless && smartTagType.imageless) {
					list.push(smartTagType);
				}else if(!imageless) {
					list.push(smartTagType);
				}

			}
		});

		return list;
	}

	/**
	 * Review if the SmartTag is valid for the Room
	 * @param smartTagType
	 * @param roomMetaData
	 * @private
	 */
	private isValidSmartTagForRoom(smartTagType: any, roomMetaData: any): boolean {
		const valid = (
			(smartTagType.viewRoom === 'ext' && roomMetaData.ext) ||
			(smartTagType.viewRoom === 'int' && roomMetaData.int) ||
			(smartTagType.viewRoom === 'park' && roomMetaData.park) ||
			(roomMetaData.both) ||
			(smartTagType.viewRoom === roomMetaData.roomName) ||
			(smartTagType.viewRoom === 'all') ||
			(smartTagType.viewRoom === undefined)
		);
		if (smartTagType.when) {
			return valid && (smartTagType.when.find(x => x.roomNameIs === roomMetaData.roomName) !== undefined)
		}
		return valid;
	}
}
